/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './SidebarPanel';

const SidebarPanelManager: ComponentManagerFn<'SidebarPanel'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'SidebarPanel'>(platform, 'SidebarPanel'),
    type: 'SidebarPanel',
    name: 'Sidebar Panel',
    isChildAllowed: (page, item, childType) =>
      (
        [
          'AvatarList',
          'MenuItemDivider',
          'SidebarDraggableList',
          'SidebarHeader',
          'SidebarList',
          'SidebarNavSection',
          'SidebarNavDropdown',
          'SidebarSpacer',
          'SidebarNavList',
          'TriggerOnClick',
          'UserList',
        ] as ItemType[]
      ).includes(childType),
    defaultValue,
    defaultChildren: [
      'SidebarNavSection',
      'MenuItemDivider',
      'SidebarHeader',
      'AvatarList',
      'SidebarDraggableList',
      'MenuItemDivider',
      'SidebarSpacer',
      'SidebarList',
    ],
    props: {
      title: {
        label: 'Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      titleColorExpression: {
        label: 'Title Color',
        type: 'ColorExpression',
      },
      icon: {
        label: 'Icon',
        type: 'iconselect',
      },
      iconColorExpression: {
        label: 'Icon Color',
        type: 'ColorExpression',
        placeholder: 'e.g. #a2a0a2 or blue or primary',
      },
      sidebarWidth: {
        label: 'Width',
        type: 'number',
        min: 200,
        max: 400,
        step: 20,
      },
      applyPagePadding: {
        label: 'Apply Page Padding',
        type: 'boolean',
        helpText:
          'If checked, the page padding will be applied to the sideBarPanel as well.',
      },
      highLightActiveTab: {
        label: 'HighLight Active Tab',
        type: 'boolean',
        helpText: 'If checked, the Active Tab in Panel  will be Highlighted',
      },
      activeSideBarTab: {
        type: 'ItemID',
        label: 'Active Sidebar Item',
        initialFilter: 'SidebarNavLink',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (
            item?.type === 'SidebarNavLink' ||
            item?.type === 'SidebarListItem'
          ) {
            return '';
          }
          return `Item id '${value}' doesn't belong to a SideBarLink or SidebarListItem`;
        },
        helpText:
          'Specify the Item ID of the SidebarLink or SidebarListItem that should be marked as active.',
      },
      highlightColor: {
        label: 'Active Tab Color',
        type: 'ColorExpression',
        placeholder: "e.g. '#FFFFFF'",
        helpText: 'Specify a background color for the Active Tab.',
      },
      highlightTextColor: {
        label: 'Active Tab Text Color',
        type: 'ColorExpression',
        placeholder: "e.g. '#157CEC'",
        helpText: 'Specify a text color for the component.',
      },
      activeHoverColor: {
        label: 'Active Tab Hover Color',
        type: 'ColorExpression',
        placeholder: "e.g. '#FFFFFF'",
        helpText: 'Specify a background color for the Active Tab.',
      },
      activeHoverTextColor: {
        label: 'Active Tab Hover Text Color',
        type: 'ColorExpression',
        placeholder: "e.g. '#157CEC'",
        helpText: 'Specify a text color for the component.',
      },
    },
    isPropertyVisible(
      prop,
      itemData,
      { appUid, pageId, itemId, platform: p },
    ): boolean {
      const parent = p.getItem(appUid, pageId, itemId)?.parent === 'root';
      switch (prop) {
        case 'applyPagePadding':
          return parent ? true : false;
        case 'activeSideBarTab':
        case 'highlightColor':
        case 'highlightTextColor':
        case 'activeHoverColor':
        case 'activeHoverTextColor':
          return itemData.highLightActiveTab;
        default:
          return true;
      }
    },
    getDisplayText: (item) => item.props.title.value,
    category: 'Sidebar',
    layout: 'sizeAware',
    canHaveChildren: () => true,
    icon: { iconName: 'sidebar' },
    description: `**Overview**<br/>
The Sidebar Panel is a structural container component used to create a collapsible side drawer within a page. It serves as the root container for all sidebar-related elements, effectively partitioning the screen into a main content area and a navigation/utility pane.
Use this component to build the primary navigation structure of an application, a persistent tools palette, or a context-aware details pane.

**1. Configuration**<br/>
* **Sidebar Width:** Sets the fixed width of the panel (200px - 400px), determining how much screen real estate it occupies when open.
* **Apply Page Padding:** If checked, the panel respects the page's global padding settings; otherwise, it likely stretches to the full height/edge of the viewport.
* **Title & Icon:** Defines the default header text and visual icon for the panel. Both support ColorExpression properties (titleColorExpression, iconColorExpression) for dynamic styling.

**2. Active Tab Management**<br/>
The panel includes built-in logic to manage the visual state of selected navigation items.
* **Highlight Active Tab:** A boolean toggle that enables the "active state" styling system.
* **Active Sidebar Item:** Accepts the ItemID of a specific SidebarNavLink or SidebarListItem that should be highlighted by default (e.g., the "Home" link).
* **Styling States:** You can granularly control the colors for the active item to match your theme (Highlight Color, Highlight Text Color, Active Hover Color, Active Hover Text Color).

**3. Structure & Children**<br/>
As a root container, the Sidebar Panel supports a wide range of specific child components designed to build rich interfaces.
* **Navigation:** SidebarNavSection, SidebarNavDropdown, SidebarList.
* **Headers & Separators:** SidebarHeader, MenuItemDivider, SidebarSpacer.
* **User & Collaboration:** AvatarList, UserList.
* **Data-Driven:** SidebarDraggableList.

**4. Best Use Cases**<br/>
* **App Shell:** Defining the main layout of an application with a left-hand navigation menu.
* **Inspector Panel:** Creating a right-hand panel that shows details for a record selected in the main grid.
* **Settings Drawer:** Grouping configuration options (User, System, Preferences) into a clean, collapsible side view.`,
  };
};

export default SidebarPanelManager;
